import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Logo from '../Logo';
import OnboardingStep1 from './OnboardingStep1';
import OnboardingStep2 from './OnboardingStep2';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import WelcomeBanner from '../WelcomeBanner';

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data collected across steps
  const [wizardData, setWizardData] = useState({
    // Step 1 data
    services: [],
    bundles: [],  // User's selected bundles
    eligibility: {
      isStudent: false,
      isSenior: false,
      isVeteran: false,
      isFirstResponder: false
    },
    creditCards: [],
    memberships: [],
    internetProvider: '',
    zipCode: '',
    
    // Step 2 data
    subscriptionDetails: [],
    cardAllocations: []
  });

  // Reference data from database
  const [refData, setRefData] = useState({
    streamingServices: [],
    bundles: [],
    ispPerks: [],
    cardPerks: [],
    membershipPerks: []
  });

  useEffect(() => {
    loadInitialData();
  }, [user]);

  const loadInitialData = async () => {
    if (!user) return;

    try {
      // Load user's current onboarding step
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('onboarding_step, zip_code, internet_provider, is_student, is_senior, is_veteran, is_first_responder')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      // Set starting step based on saved progress
      if (userData?.onboarding_step === 1) {
        setCurrentStep(2);
      }

      // Load any existing user data
      const { data: existingCards } = await supabase
        .from('user_credit_cards')
        .select('*')
        .eq('user_id', user.id);

      const { data: existingMemberships } = await supabase
        .from('user_memberships')
        .select('*')
        .eq('user_id', user.id);

      const { data: existingSubscriptions } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id);

      // =====================================================
      // NEW: Load existing user bundles from user_bundles table
      // =====================================================
      const { data: existingUserBundles } = await supabase
        .from('user_bundles')
        .select('*, bundles(id, bundle_name, bundle_code, monthly_cost, notes)')
        .eq('user_id', user.id)
        .eq('is_active', true);

      console.log('Loaded existing user bundles:', existingUserBundles);

      // Load reference data
      const { data: streamingServices } = await supabase
        .from('streaming_services')
        .select('*')
        .eq('is_active', true)
        .order('service_name');

      const { data: bundles } = await supabase
        .from('bundles')
        .select('*, bundle_services(*)')
        .eq('is_active', true)
        .order('monthly_cost');

      const { data: ispPerks } = await supabase
        .from('isp_perks')
        .select('*')
        .eq('is_active', true);

      const { data: cardPerks } = await supabase
        .from('card_perks')
        .select('*')
        .eq('is_active', true);

      const { data: membershipPerks } = await supabase
        .from('membership_perks')
        .select('*')
        .eq('is_active', true);

      setRefData({
        streamingServices: streamingServices || [],
        bundles: bundles || [],
        ispPerks: ispPerks || [],
        cardPerks: cardPerks || [],
        membershipPerks: membershipPerks || []
      });

      // =====================================================
      // Transform existing user bundles to the format Step1 expects
      // =====================================================
      const transformedBundles = (existingUserBundles || []).map(ub => ({
        bundle_code: ub.bundle_code,
        bundle_name: ub.bundles?.bundle_name || ub.bundle_code,
        monthly_cost: ub.monthly_cost || ub.bundles?.monthly_cost || 0,
        // Include the full bundle data for service lookups
        bundle_services: bundles?.find(b => b.bundle_code === ub.bundle_code)?.bundle_services || []
      }));

      console.log('Transformed bundles for wizard:', transformedBundles);

      // Pre-populate wizard data with existing user data
      setWizardData(prev => ({
        ...prev,
        services: existingSubscriptions?.map(s => s.service_name) || [],
        bundles: transformedBundles,  // NEW: Include user's bundles
        creditCards: existingCards || [],
        memberships: existingMemberships || [],
        internetProvider: userData?.internet_provider || '',
        zipCode: userData?.zip_code || '',
        eligibility: {
          isStudent: userData?.is_student || false,
          isSenior: userData?.is_senior || false,
          isVeteran: userData?.is_veteran || false,
          isFirstResponder: userData?.is_first_responder || false
        }
      }));

      setLoading(false);
    } catch (error) {
      console.error('Error loading onboarding data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const updateWizardData = (newData) => {
    setWizardData(prev => ({ ...prev, ...newData }));
  };

  const handleStep1Complete = async (step1Data) => {
    setSaving(true);
    
    try {
      // Update user profile
      const { error: userError } = await supabase
        .from('users')
        .update({
          zip_code: step1Data.zipCode,
          internet_provider: step1Data.internetProvider,
          is_student: step1Data.eligibility.isStudent,
          is_senior: step1Data.eligibility.isSenior,
          is_veteran: step1Data.eligibility.isVeteran,
          is_first_responder: step1Data.eligibility.isFirstResponder,
          onboarding_step: 1,
          onboarding_started_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (userError) throw userError;

      // Save credit cards (delete existing first, then insert new)
      await supabase
        .from('user_credit_cards')
        .delete()
        .eq('user_id', user.id);

      if (step1Data.creditCards.length > 0) {
        const cardsToInsert = step1Data.creditCards.map(card => ({
          user_id: user.id,
          card_name: card.card_name,
          issuer: card.issuer,
          streaming_credit_monthly: card.streaming_credit_monthly || 0
        }));

        const { error: cardsError } = await supabase
          .from('user_credit_cards')
          .insert(cardsToInsert);

        if (cardsError) throw cardsError;
      }

      // Save memberships
      await supabase
        .from('user_memberships')
        .delete()
        .eq('user_id', user.id);

      if (step1Data.memberships.length > 0) {
        const membershipsToInsert = step1Data.memberships.map(m => ({
          user_id: user.id,
          membership_name: m.membership_name,
          membership_type: m.membership_type
        }));

        const { error: membsError } = await supabase
          .from('user_memberships')
          .insert(membershipsToInsert);

        if (membsError) throw membsError;
      }

      // =====================================================
      // NEW: Save/update user bundles
      // =====================================================
      // First, deactivate all existing bundles
      await supabase
        .from('user_bundles')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      // Then insert/upsert the selected bundles
      if (step1Data.bundles && step1Data.bundles.length > 0) {
        for (const bundle of step1Data.bundles) {
          const { error: bundleError } = await supabase
            .from('user_bundles')
            .upsert({
              user_id: user.id,
              bundle_code: bundle.bundle_code,
              monthly_cost: bundle.monthly_cost || 0,
              is_active: true,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id,bundle_code'
            });

          if (bundleError) {
            console.error('Error saving bundle:', bundleError);
          }
        }
      }

      // Save subscriptions (just service names for now, details in Step 2)
      await supabase
        .from('subscriptions')
        .delete()
        .eq('user_id', user.id);

      if (step1Data.services.length > 0) {
        const subsToInsert = step1Data.services.map(service => ({
          user_id: user.id,
          service_name: service,
          monthly_cost: 0,
          billing_cycle: 'monthly',
          payment_method: 'paid'
        }));

        const { error: subsError } = await supabase
          .from('subscriptions')
          .insert(subsToInsert);

        if (subsError) throw subsError;
      }

      // Update wizard data and move to step 2
      updateWizardData(step1Data);
      setCurrentStep(2);
      toast.success('Progress saved!');
      
    } catch (error) {
      console.error('Error saving step 1:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to save active perks
  const saveActivePerks = async (membershipPerkSelections, ispPerkSelections) => {
    // Clear existing active perks for this user
    await supabase
      .from('active_perks')
      .delete()
      .eq('user_id', user.id);
    
    const perksToInsert = [];
    const today = new Date().toISOString().split('T')[0];
    
    // Helper to generate UUID
    const generateUUID = () => {
      return crypto.randomUUID ? crypto.randomUUID() : 
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
    };
    
    // Add membership perks
    if (membershipPerkSelections && membershipPerkSelections.length > 0) {
      membershipPerkSelections.forEach(perk => {
        perksToInsert.push({
          id: generateUUID(),
          user_id: user.id,
          perk_source: perk.perk_source,
          service_name: perk.service_name,
          tier: perk.tier || null,
          activated_date: today,
          is_active: true
        });
      });
    }
    
    // Add ISP perks
    if (ispPerkSelections && ispPerkSelections.length > 0) {
      ispPerkSelections.forEach(perk => {
        perksToInsert.push({
          id: generateUUID(),
          user_id: user.id,
          perk_source: perk.perk_source,
          service_name: perk.service_name,
          tier: perk.tier || null,
          activated_date: today,
          is_active: true
        });
      });
    }
    
    if (perksToInsert.length > 0) {
      const { error } = await supabase
        .from('active_perks')
        .insert(perksToInsert);
      
      if (error) {
        console.error('Error saving active perks:', error);
        throw error;
      }
    }
  };

  // Helper function to save credit allocations and bundle details
  const saveAllocationsAndBundles = async (step2Data, userCards, userSubs) => {
    // Insert allocations for SERVICES and BUNDLES
    for (const allocation of step2Data.cardAllocations) {
      const card = userCards?.find(c => c.card_name === allocation.cardName);
      if (!card) continue;

      // Service allocations
      if (allocation.serviceAllocations) {
        for (const serviceAlloc of allocation.serviceAllocations) {
          const sub = userSubs?.find(s => s.service_name === serviceAlloc.service);
          if (!sub || !serviceAlloc.amount) continue;

          await supabase
            .from('credit_card_allocations')
            .insert({
              user_id: user.id,
              credit_card_id: card.id,
              subscription_id: sub.id,
              amount_allocated: serviceAlloc.amount,
              target_type: 'subscription'
            });
          
          // Update the subscription's credit_applied field
          const subMonthlyCost = parseFloat(sub.monthly_cost) || 0;
          await supabase
            .from('subscriptions')
            .update({
              credit_applied: serviceAlloc.amount,
              actual_cost: Math.max(0, subMonthlyCost - serviceAlloc.amount)
            })
            .eq('id', sub.id);
        }
      }

      // Bundle allocations
      if (allocation.bundleAllocations) {
        for (const bundleAlloc of allocation.bundleAllocations) {
          // Find the bundle reference by name
          const bundleRef = refData.bundles?.find(b => b.bundle_name === bundleAlloc.bundle);
          if (!bundleRef) continue;
          
          // Find the user's bundle record
          const { data: userBundle } = await supabase
            .from('user_bundles')
            .select('id, bundle_code, monthly_cost')
            .eq('user_id', user.id)
            .eq('bundle_code', bundleRef.bundle_code)
            .eq('is_active', true)
            .single();
          
          if (!userBundle || !bundleAlloc.amount) continue;

          // Insert allocation record
          await supabase
            .from('credit_card_allocations')
            .insert({
              user_id: user.id,
              credit_card_id: card.id,
              bundle_id: userBundle.id,
              subscription_id: null,
              amount_allocated: bundleAlloc.amount,
              target_type: 'bundle'
            });
          
          // Update the bundle's credit_applied and actual_cost
          const monthlyCost = parseFloat(userBundle.monthly_cost) || 0;
          await supabase
            .from('user_bundles')
            .update({
              credit_applied: bundleAlloc.amount,
              actual_cost: Math.max(0, monthlyCost - bundleAlloc.amount)
            })
            .eq('id', userBundle.id);
        }
      }
    }

    // Update bundle payment details and create subscription records for bundled services
    if (step2Data.bundles && step2Data.bundles.length > 0) {
      for (const bundleData of step2Data.bundles) {
        const monthlyCost = bundleData.billingCycle === 'annual' 
          ? (bundleData.annualCost || 0) / 12 
          : (bundleData.monthlyCost || 0);
        
        // First, get the current bundle to check if credits were applied
        const { data: currentBundle } = await supabase
          .from('user_bundles')
          .select('credit_applied')
          .eq('user_id', user.id)
          .eq('bundle_code', bundleData.bundleCode)
          .single();
        
        const creditApplied = parseFloat(currentBundle?.credit_applied) || 0;
        const actualCost = Math.max(0, monthlyCost - creditApplied);
        
        // Update the bundle's cost, preserving credit_applied
        await supabase
          .from('user_bundles')
          .update({
            monthly_cost: monthlyCost,
            actual_cost: actualCost,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('bundle_code', bundleData.bundleCode);
        
        // Create/update subscription records for bundled services (for waterfall retail calculation)
        const bundleRef = refData.bundles?.find(b => b.bundle_code === bundleData.bundleCode);
        if (bundleRef?.bundle_services) {
          for (const bs of bundleRef.bundle_services) {
            // Check if subscription already exists
            const existingSub = userSubs?.find(s => 
              s.service_name.toLowerCase() === bs.service_name.toLowerCase()
            );
            
            // Look up retail price
            const serviceRef = refData.streamingServices?.find(
              s => s.service_name.toLowerCase() === bs.service_name.toLowerCase()
            );
            const retailCost = serviceRef?.base_price_monthly || 0;
            
            if (existingSub) {
              // Update existing subscription to be part of bundle
              await supabase
                .from('subscriptions')
                .update({
                  payment_method: 'bundle',
                  payment_source: bundleData.bundleCode,
                  monthly_cost: 0,
                  retail_cost: retailCost,
                  actual_cost: 0,
                  subscription_tier: bs.included_tier || 'Standard'
                })
                .eq('id', existingSub.id);
            } else {
              // Create new subscription record for the bundled service
              await supabase
                .from('subscriptions')
                .insert({
                  user_id: user.id,
                  service_name: bs.service_name,
                  subscription_tier: bs.included_tier || 'Standard',
                  payment_method: 'bundle',
                  payment_source: bundleData.bundleCode,
                  monthly_cost: 0,
                  retail_cost: retailCost,
                  actual_cost: 0,
                  billing_cycle: 'monthly'
                });
            }
          }
        }
      }
    }
  };

  // Save Step 2 progress WITHOUT marking complete
  const handleStep2Save = async (step2Data) => {
    setSaving(true);
    
    try {
      // Update subscriptions with full details
      for (const sub of step2Data.subscriptionDetails) {
        // Look up retail price from streaming_services reference table
        const refService = refData.streamingServices?.find(
          s => s.service_name.toLowerCase() === sub.service.toLowerCase()
        );
        const retailCost = refService?.base_price_monthly || sub.cost || 0;
        
        await supabase
          .from('subscriptions')
          .update({
            subscription_tier: sub.tier,
            billing_cycle: sub.billingCycle || 'monthly',
            payment_method: sub.paymentMethod,
            payment_source: sub.paymentSource,
            monthly_cost: sub.cost || 0,
            retail_cost: retailCost,
            credit_applied: sub.creditApplied || 0,
            actual_cost: sub.actualCost || sub.cost || 0
          })
          .eq('user_id', user.id)
          .eq('service_name', sub.service);
      }

      // Save card allocations
      const { data: userCards } = await supabase
        .from('user_credit_cards')
        .select('id, card_name')
        .eq('user_id', user.id);

      // Clear existing allocations
      if (userCards && userCards.length > 0) {
        const cardIds = userCards.map(c => c.id);
        await supabase
          .from('credit_card_allocations')
          .delete()
          .in('credit_card_id', cardIds);
      }

      // Get current subscription IDs
      const { data: userSubs } = await supabase
        .from('subscriptions')
        .select('id, service_name')
        .eq('user_id', user.id);

      // Insert new allocations and save bundle details
      await saveAllocationsAndBundles(step2Data, userCards, userSubs);

      // Save active perks (NEW)
      await saveActivePerks(step2Data.membershipPerkSelections, step2Data.ispPerkSelections);

      // Update timestamp but DON'T mark complete
      await supabase
        .from('users')
        .update({
          onboarding_step: 2,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      toast.success('Progress saved!');
      
    } catch (error) {
      console.error('Error saving step 2:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  // Save Step 2 AND mark onboarding complete
  const handleStep2Complete = async (step2Data) => {
    setSaving(true);
    
    try {
      // Update subscriptions with full details
      for (const sub of step2Data.subscriptionDetails) {
        // Look up retail price from streaming_services reference table
        const refService = refData.streamingServices?.find(
          s => s.service_name.toLowerCase() === sub.service.toLowerCase()
        );
        const retailCost = refService?.base_price_monthly || sub.cost || 0;
        
        await supabase
          .from('subscriptions')
          .update({
            subscription_tier: sub.tier,
            billing_cycle: sub.billingCycle || 'monthly',
            payment_method: sub.paymentMethod,
            payment_source: sub.paymentSource,
            monthly_cost: sub.cost || 0,
            retail_cost: retailCost,
            credit_applied: sub.creditApplied || 0,
            actual_cost: sub.actualCost || sub.cost || 0
          })
          .eq('user_id', user.id)
          .eq('service_name', sub.service);
      }

      // Save card allocations
      const { data: userCards } = await supabase
        .from('user_credit_cards')
        .select('id, card_name')
        .eq('user_id', user.id);

      // Clear existing allocations
      if (userCards && userCards.length > 0) {
        const cardIds = userCards.map(c => c.id);
        await supabase
          .from('credit_card_allocations')
          .delete()
          .in('credit_card_id', cardIds);
      }

      // Get current subscription IDs
      const { data: userSubs } = await supabase
        .from('subscriptions')
        .select('id, service_name')
        .eq('user_id', user.id);

      // Insert new allocations and save bundle details
      await saveAllocationsAndBundles(step2Data, userCards, userSubs);

      // Save active perks (NEW)
      await saveActivePerks(step2Data.membershipPerkSelections, step2Data.ispPerkSelections);

      // NOW mark onboarding complete
      await supabase
        .from('users')
        .update({
          onboarding_step: 2,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      toast.success('Profile setup complete!');
      
      if (onComplete) {
        onComplete();
      }
      
    } catch (error) {
      console.error('Error completing step 2:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    // Save current progress before skipping
    try {
      await supabase
        .from('users')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
    } catch (error) {
      console.error('Error saving on skip:', error);
    }
    
    if (onSkip) {
      onSkip();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 relative">
      <Toaster position="top-right" />
      <div className="absolute top-4 left-4">
        <WelcomeBanner variant="gradient" />
      </div>
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="text-center mb-6 pt-6">
          <Logo size="default" variant="glow" />
          <p className="text-purple-200 text-lg mt-2">Complete Your Profile</p>
        </div>

        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-white' : 'text-purple-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                currentStep >= 1 ? 'bg-purple-500' : 'bg-purple-800'
              }`}>
                {currentStep > 1 ? '✓' : '1'}
              </div>
              <span className="hidden sm:inline">Your Services</span>
            </div>
            
            <div className={`w-12 h-1 ${currentStep >= 2 ? 'bg-purple-500' : 'bg-purple-800'}`} />
            
            <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-white' : 'text-purple-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                currentStep >= 2 ? 'bg-purple-500' : 'bg-purple-800'
              }`}>
                2
              </div>
              <span className="hidden sm:inline">Payment Details</span>
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          {currentStep === 1 && (
            <OnboardingStep1
              initialData={wizardData}
              refData={refData}
              onComplete={handleStep1Complete}
              onSkip={handleSkip}
              saving={saving}
            />
          )}
          
          {currentStep === 2 && (
            <OnboardingStep2
              wizardData={wizardData}
              refData={refData}
              onSave={handleStep2Save}
              onComplete={handleStep2Complete}
              onBack={handleBack}
              onSkip={handleSkip}
              saving={saving}
            />
          )}
        </div>

        {/* Skip link */}
        <div className="text-center mt-4">
          <button
            onClick={handleSkip}
            className="text-purple-300 hover:text-white text-sm underline"
          >
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;